'use client'

import { Label } from '@/components/ui/label'
import React, { useState, useEffect, useMemo } from 'react'
import { debounce } from 'throttle-debounce'
import { useRouter } from 'next/navigation'
import { SearchIcon } from 'lucide-react'
import { ClassValue } from 'clsx'
import { cn } from '@/utilities/ui'

interface Props {
    classes?: {
        container?: ClassValue
        input?: ClassValue
        icon?: ClassValue
    }
    placeholder?: string
    searchWhileTyping?: boolean
    onSubmit?: () => void
    searchValue?: string
}

export const SearchForm: React.FC<Props> = ({ classes, onSubmit, searchWhileTyping, searchValue = '', placeholder = 'Search' }) => {
    const [value, setValue] = useState('')
    const [debouncedValue, setDebouncedValue] = useState('')
    const debouncedSetValue = useMemo(() => debounce(searchWhileTyping ? 800 : 300, setDebouncedValue), [setDebouncedValue, searchWhileTyping])
    const router = useRouter()

    useEffect(() => {
        // Set query parameter as input value
        setValue(searchValue)
    }, [searchValue])

    useEffect(() => {
        return () => {
            debouncedSetValue.cancel({ upcomingOnly: true })
        }
    }, [debouncedSetValue])

    useEffect(() => {
        if (searchWhileTyping && debouncedValue) {
            router.push(`/search?q=${debouncedValue}`)
        }
    }, [debouncedValue, router, searchWhileTyping])

    return (
        <div className={cn(classes?.container)}>
            <form
                onSubmit={(e) => {
                    e.preventDefault()
                    onSubmit?.()

                    if (!searchWhileTyping && debouncedValue) {
                        router.push(`/search?q=${debouncedValue}`)
                    }
                }}
            >
                <Label htmlFor='search' className='sr-only text-accent font-sans font-light'>
                    Search For
                </Label>

                <div className='relative'>
                    <input
                        type='search'
                        id='search-dropdown'
                        className={cn(
                            'block p-2.5 w-full text-sm text-gray-900 bg-gray-50 border-accent border-b focus:ring-none focus:outline-none [&::-webkit-search-cancel-button]:hidden',
                            classes?.input,
                        )}
                        placeholder={placeholder}
                        value={value}
                        onChange={(e) => {
                            setValue(e.target.value)
                            debouncedSetValue(e.target.value)
                        }}
                        required
                    />
                    <button type='submit' className='absolute top-0 end-0 p-2.5 text-sm font-medium h-full focus:ring-none focus:outline-none'>
                        <SearchIcon size={20} className={cn(classes?.icon)} />
                        <span className='sr-only'>Search</span>
                    </button>
                </div>
            </form>
        </div>
    )
}
